/**
 * Déduction archétype produit existant — Lot L-UX-1 (étape 1).
 * Règles : docs/platform/SPEC-ASSISTANT-FICHE-PRODUIT.md §4
 */

import { ARCHETYPE_IDS } from '../config/produitArchetypes.js';

/**
 * @typedef {Object} DeduceResult
 * @property {string} archetypeId
 * @property {boolean} ambiguous
 * @property {string[]} signals
 */

function normalizeOptions(produit) {
  const options = produit?.options;
  return Array.isArray(options) ? options : [];
}

function hasPlateauxLinks(produit) {
  const fromIds = Array.isArray(produit?.plateauxIdsDisponibles) && produit.plateauxIdsDisponibles.length > 0;
  const fromRelations =
    Array.isArray(produit?.ingredientsPlanche) && produit.ingredientsPlanche.length > 0;
  return fromIds || fromRelations;
}

function isSaucesPattern(produit) {
  const options = normalizeOptions(produit);
  if (options.length === 0) return false;

  const hasSauceNamedGroup = options.some((opt) =>
    String(opt?.nom ?? '').toLowerCase().includes('sauce')
  );

  const groupsWithFreeChoice = options.filter((opt) => {
    const choix = opt?.choix ?? opt?.choices ?? [];
    return choix.some((c) => Number(c?.prix ?? 0) === 0);
  });

  const groupsWithPaidChoice = options.filter((opt) => {
    const choix = opt?.choix ?? opt?.choices ?? [];
    return choix.some((c) => Number(c?.prix ?? 0) > 0);
  });

  const hasFreeAndPaidPattern =
    groupsWithFreeChoice.length > 0 && groupsWithPaidChoice.length > 0;

  return hasSauceNamedGroup || hasFreeAndPaidPattern;
}

function isFormulePattern(produit) {
  if (produit?.productType === 'MENU_COMBO') return true;
  const options = normalizeOptions(produit);
  return options.length >= 3;
}

function collectSignals(produit) {
  const signals = [];
  if (produit?.modeComposition) signals.push('modeComposition');
  if (produit?.venteAuPoids) signals.push('venteAuPoids');
  if (produit?.productType === 'PAR_PERSONNE') signals.push('PAR_PERSONNE');
  if (produit?.compositionMode === 'COMPOSABLE') signals.push('compositionMode:COMPOSABLE');
  if (produit?.productType === 'MENU_COMBO') signals.push('MENU_COMBO');
  if (produit?.productType === 'COMMERCE_NON_FOOD') signals.push('COMMERCE_NON_FOOD');
  if (hasPlateauxLinks(produit)) signals.push('plateauxLinks');
  if (normalizeOptions(produit).length > 0) signals.push('hasOptions');
  if (produit?.elementsComposables?.length > 0) signals.push('elementsComposables');
  return signals;
}

function isAmbiguousCombination(produit) {
  const modeComposition = Boolean(produit?.modeComposition);
  const parPersonne = produit?.productType === 'PAR_PERSONNE';
  const venteAuPoids = Boolean(produit?.venteAuPoids);

  if (modeComposition && parPersonne) return true;
  if (modeComposition && venteAuPoids) return true;
  if (parPersonne && venteAuPoids) return true;
  return false;
}

/**
 * @param {Object|null|undefined} produit
 * @returns {DeduceResult}
 */
export function deduceProduitArchetype(produit) {
  if (!produit || typeof produit !== 'object') {
    return {
      archetypeId: ARCHETYPE_IDS.SIMPLE,
      ambiguous: false,
      signals: [],
    };
  }

  const signals = collectSignals(produit);

  if (isAmbiguousCombination(produit)) {
    return {
      archetypeId: ARCHETYPE_IDS.AVANCE,
      ambiguous: true,
      signals,
    };
  }

  if (produit.modeComposition) {
    return { archetypeId: ARCHETYPE_IDS.PLATEAU_PIECES, ambiguous: false, signals };
  }

  if (produit.venteAuPoids) {
    return { archetypeId: ARCHETYPE_IDS.POIDS, ambiguous: false, signals };
  }

  if (produit.productType === 'PAR_PERSONNE') {
    if (produit.compositionMode === 'COMPOSABLE') {
      return { archetypeId: ARCHETYPE_IDS.PLANCHE_PERSO, ambiguous: false, signals };
    }
    return { archetypeId: ARCHETYPE_IDS.PLANCHE_PERSONNES, ambiguous: false, signals };
  }

  if (produit.productType === 'MENU_COMBO' || isFormulePattern(produit)) {
    return { archetypeId: ARCHETYPE_IDS.FORMULE, ambiguous: false, signals };
  }

  if (hasPlateauxLinks(produit)) {
    return { archetypeId: ARCHETYPE_IDS.INGREDIENT_PLANCHE, ambiguous: false, signals };
  }

  if (produit.productType === 'COMMERCE_NON_FOOD') {
    return { archetypeId: ARCHETYPE_IDS.RETAIL, ambiguous: false, signals };
  }

  if (isSaucesPattern(produit)) {
    return { archetypeId: ARCHETYPE_IDS.SAUCES, ambiguous: false, signals };
  }

  const options = normalizeOptions(produit);
  if (options.length > 0) {
    return { archetypeId: ARCHETYPE_IDS.AVEC_OPTIONS, ambiguous: false, signals };
  }

  return { archetypeId: ARCHETYPE_IDS.SIMPLE, ambiguous: false, signals };
}

export function applyArchetypePrefill(formData, archetypeId, getArchetypeByIdFn) {
  const archetype = getArchetypeByIdFn(archetypeId);
  if (!archetype?.prefill) return { ...formData };
  return { ...formData, ...archetype.prefill };
}
